// "Does this browser hold a passkey for this user?" is not a question
// WebAuthn will answer: there is no silent "do you have a credential for this
// RP" query, deliberately, since that would be a fingerprinting oracle. The
// server can only answer the *account*-level question — does this user have a
// credential anywhere — and answering the account question is what left a
// second device (a new phone, a fresh PWA install) permanently un-enrolled:
// the account already had a passkey from the first device, so nothing ever
// offered to add one here.
//
// So the browser records it itself. Enrolling a passkey here, or signing in
// with one here, marks the device; the absence of a mark is what makes the
// enrolment prompt appear. A mark can be lost (cleared site data, storage
// unavailable) — the cost is one extra prompt, and the platform's own
// excludeCredentials check resolves it (see EnrolPasskey's InvalidStateError
// handling), so this store is never the sole authority for anything.

const DEFAULT_STORAGE_KEY = "ferrivia.passkey-device";

// The credential record lives under its own key rather than inside the
// per-user map above, because it answers a question asked *before* anyone is
// signed in — "which passkey does this browser use?" — and so cannot be keyed
// by a user id the caller does not have yet.
function credentialKeyFor(storageKey: string): string {
  return `${storageKey}.credential`;
}

// A skip has to outlive the tab it happened in, or every app launch re-asks
// a user who has already said no. It must not outlive it by much either:
// the same person on their everyday device is exactly who we want on a
// passkey.
export const SKIP_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

interface DeviceRecord {
  enrolled?: boolean;
  skippedAt?: number;
}

type DeviceRecords = Record<string, DeviceRecord>;

/**
 * The passkey this browser last used. Naming it in `allowCredentials` is what
 * stops the platform asking which credential to use — see "Naming the
 * credential" in `platform/docs/auth.md`.
 *
 * `transports` is only knowable at registration (an assertion response has no
 * transports field), so a record written by a login carries the id alone.
 */
export interface DeviceCredential {
  id: string;
  transports?: string[];
}

export interface PasskeyDeviceStore {
  /** Has a passkey been enrolled or used for this user in this browser? */
  isEnrolled(userId: string): boolean;
  markEnrolled(userId: string): void;
  /** The server says this account has no credentials, so our mark is stale. */
  clearEnrolled(userId: string): void;
  isSkipRecent(userId: string, now?: number): boolean;
  markSkipped(userId: string, now?: number): void;
  /** The credential to ask for by name, or null to let the platform ask. */
  lastCredential(): DeviceCredential | null;
  rememberCredential(credential: DeviceCredential): void;
  /** It did not work, or the account has none — stop naming it. */
  forgetCredential(): void;
}

export function createPasskeyDeviceStore(storageKey = DEFAULT_STORAGE_KEY): PasskeyDeviceStore {
  function readAll(): DeviceRecords {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as DeviceRecords) : {};
    } catch {
      // Unavailable or corrupt storage — the prompt just shows once more.
      return {};
    }
  }

  function update(userId: string, patch: DeviceRecord): void {
    try {
      const all = readAll();
      all[userId] = { ...all[userId], ...patch };
      localStorage.setItem(storageKey, JSON.stringify(all));
    } catch {
      // see readAll
    }
  }

  function isEnrolled(userId: string): boolean {
    return readAll()[userId]?.enrolled === true;
  }

  function markEnrolled(userId: string): void {
    update(userId, { enrolled: true, skippedAt: undefined });
  }

  function clearEnrolled(userId: string): void {
    update(userId, { enrolled: false });
  }

  function isSkipRecent(userId: string, now = Date.now()): boolean {
    const skippedAt = readAll()[userId]?.skippedAt;
    return typeof skippedAt === "number" && now - skippedAt < SKIP_COOLDOWN_MS;
  }

  function markSkipped(userId: string, now = Date.now()): void {
    update(userId, { skippedAt: now });
  }

  function lastCredential(): DeviceCredential | null {
    try {
      const raw = localStorage.getItem(credentialKeyFor(storageKey));
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        return null;
      }
      const { id, transports } = parsed as DeviceCredential;
      // An id is the whole point; a record without one is corrupt, and
      // returning it would name nothing while suppressing the picker.
      return typeof id === "string" && id.length > 0
        ? { id, ...(Array.isArray(transports) ? { transports } : {}) }
        : null;
    } catch {
      // see readAll — no record means no allowlist, which is the old behaviour.
      return null;
    }
  }

  function rememberCredential(credential: DeviceCredential): void {
    try {
      // Merge when the id is unchanged: a login knows the id but not the
      // transports, and must not blank out what a registration recorded.
      const existing = lastCredential();
      const transports =
        credential.transports ?? (existing?.id === credential.id ? existing.transports : undefined);
      const record: DeviceCredential = { id: credential.id, ...(transports ? { transports } : {}) };
      localStorage.setItem(credentialKeyFor(storageKey), JSON.stringify(record));
    } catch {
      // see readAll
    }
  }

  function forgetCredential(): void {
    try {
      localStorage.removeItem(credentialKeyFor(storageKey));
    } catch {
      // see readAll
    }
  }

  return {
    isEnrolled,
    markEnrolled,
    clearEnrolled,
    isSkipRecent,
    markSkipped,
    lastCredential,
    rememberCredential,
    forgetCredential,
  };
}
